/**
 * Tooltip Component - Simple, accessible tooltip using CSS
 * Issue #500 - Waitlist Polish Features
 * Issue #791 - Enhanced to support ReactNode content and improved accessibility
 */

import React, { ReactNode, useId } from 'react';

interface TooltipProps {
  /** Content to show in the tooltip (supports both string and ReactNode) */
  content: string | ReactNode;
  /** The element to wrap with tooltip */
  children: ReactNode;
  /** Tooltip position (default: 'top') */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Additional CSS classes for the wrapper */
  className?: string;
  /** Allow wrapping for longer content (default: false for whitespace-nowrap) */
  allowWrap?: boolean;
  /** Max width for tooltip content (default: none) */
  maxWidth?: string;
}

/**
 * Lightweight tooltip component
 * Uses CSS for positioning and visibility
 * WCAG 2.1 AA compliant with proper ARIA relationships and keyboard support
 */
function Tooltip({
  content,
  children,
  position = 'top',
  className = '',
  allowWrap = false,
  maxWidth
}: TooltipProps) {
  // Generate unique ID for aria-describedby relationship (WCAG 2.1 AA - 1.3.1)
  const tooltipId = useId();
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-900',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-900',
  };

  return (
    <div className={`relative inline-block group ${className}`}>
      {/* Clone children and add aria-describedby for WCAG 2.1 AA compliance */}
      {React.isValidElement(children) ? (
        React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
          'aria-describedby': tooltipId,
        })
      ) : (
        <span aria-describedby={tooltipId}>{children}</span>
      )}

      {/* Tooltip */}
      <div
        id={tooltipId}
        className={`
          absolute z-50 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg
          pointer-events-none opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
          transition-opacity duration-200
          ${allowWrap ? '' : 'whitespace-nowrap'}
          ${positionClasses[position]}
        `}
        style={maxWidth ? { maxWidth } : undefined}
        role="tooltip"
        aria-live="polite"
      >
        {content}

        {/* Arrow */}
        <div
          className={`
            absolute w-0 h-0
            border-4
            ${arrowClasses[position]}
          `}
        />
      </div>
    </div>
  );
}

export default Tooltip;
